///
/// EarthBrowser
///
/// Created by Matt Giger on 3/4/2025
/// Copyright (c) 2025 Geodata Labs
///

import { PrecipFlowTextureSeries } from "@/earthbrowser/dataset-distribution/imerg/PrecipFlowTextureSeries"
import { CloudFieldBaker } from "@/earthbrowser/dataset-distribution/clouds/CloudFieldBaker"
import { type CloudMosaicFrameProperties } from "@/earthbrowser/dataset-distribution/clouds/CloudMosaicTable"
import { CloudMosaicWindow, type CloudRegionBinding } from "@/earthbrowser/dataset-distribution/clouds/CloudMosaicWindow"
import { type StormCloudTextureFrame } from "@/earthbrowser/dataset-distribution/clouds/StormCloudTextureFrame"
import { type RadarTextureFrame } from "@/earthbrowser/dataset-distribution/mrms/RadarTextureFrame"
import { TextureOverlay } from "@/earthbrowser/datasets/TextureOverlay"
import { TextureSeries } from "@/earthbrowser/datasets/TextureSeries"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { geographic } from "@/earthbrowser/utils/geographic"
import { cloudStyle } from "@/earthbrowser/utils/renderMode"
import { registerCloudDetailProvider } from "@/earthbrowser/utils/tileStatsRegistry"
import { type Material } from "@stovokor/shader/Material"
import { type RenderState } from "@stovokor/engine/RenderState"
import { clamp, trig } from "@stovokor/math/math"
import { timedelta } from "@stovokor/math/time"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { vec4 } from "@stovokor/math/vector"

// The overlay's visibility ramp lives in the shader (overlay_clouds.glsl
// cloudDistanceScale): per fragment, by distance from the camera, so a
// tilted view keeps its deck toward the horizon and the 2D/3D crane (which
// preserves the distance to the subject) doesn't switch the layer on or off

// Storm cutout selection: the storm whose box center is nearest the point
// under the camera wins, if within this many degrees; it keeps the slot
// until another storm is closer by the hysteresis margin, so a camera
// between two storms doesn't flip the region every frame
const cRegionSelectDeg = 25
const cRegionHysteresisDeg = 3
// Wall-clock fade when the active region appears, changes or vanishes
const cRegionFadeSeconds = 0.6
/** Scan cadence of the storm cutouts (frame duration) */
export const cRegionFrameMinutes = 10

// Forecast hand-off: the detail sources (storm cutout, mosaic window) hold
// their newest frame this long past it (StormCloudTextureSeries.endOffset,
// CloudMosaicWindow cStaleAfterMs) and live "now" sits well inside that,
// so the RRFS forecast waits until they have let go before fading in over
// the held global frame...
const cForecastLagMs = 40 * 60_000
// ...across this much clock time
const cForecastSpanMs = 10 * 60_000

/** A regional cloud frame the shader's detail path can draw: its box and pixels per side */
export interface CloudRegionFrame extends RadarTextureFrame {
	readonly bounds: vec4
	readonly size: number
}

/** Series of regional frames with per-frame boxes (storm cutouts, forecast IR) */
class RegionCloudTextureSeries<FrameType extends CloudRegionFrame> extends TextureSeries<FrameType> {
	constructor(context: EarthBrowserContext, holdPastNewest: timedelta) {
		super(context)
		this.preloadCount = 6
		this.backPreloadCount = 2
		this.purgeUnused = true
		this.endOffset = holdPastNewest
	}

	/** The loaded frame whose texture is bound as texture0/texture1
	 *  (undefined for the blank texture or a frame still loading, whose
	 *  texture is the shared blank) */
	public frameOf(texture: Texture): FrameType | undefined {
		return this.frames.find(frame => frame.available && frame.texture === texture)
	}

	/** Start time (ms) of the newest frame, undefined when empty */
	public get newestFrameMs(): number | undefined {
		const last = this.frames[this.frames.length - 1]
		return last?.range.start.date.getTime()
	}

	/** The current frame pair as a region binding once the displayed frame is loaded */
	public binding(): CloudRegionBinding | undefined {
		const frame0 = this.frameOf(this.texture0)
		if (!frame0) {
			return undefined
		}
		const frame1 = this.frameOf(this.texture1) ?? frame0
		return {
			texture0: frame0.texture,
			texture1: frame1.texture,
			bounds0: frame0.bounds,
			bounds1: frame1.bounds,
			blend: frame1 !== frame0 ? this.blendFactor : 0,
			texel: 1 / frame0.size,
		}
	}
}

/**
 * Hourly GMGSI infrared cloud overlay, plus storm-centered ~2 km cutouts.
 *
 * Global frames are 4096x2048 R8 textures with mipmaps (~11MB GPU, the same
 * as the old 2048 RGBA frames). The series purges outside its preload
 * window like the radar overlays do, so scrubbing the 48-frame timeline
 * stays bounded; purged frames reload quickly from the SQLite image cache.
 *
 * Consecutive global frames are blended along an estimated optical-flow
 * field (PrecipFlowTextureSeries - the estimator is field-agnostic) so
 * cloud systems translate over the hour instead of cross-dissolving into
 * a double image. The advected blend is baked once per change into a
 * single R8 field (CloudFieldBaker) that the tile shader samples, rather
 * than evaluated at each of its many taps per fragment.
 *
 * For active tropical cyclones a second series carries 10-minute GOES /
 * Himawari cutouts (StormCloudTextureFrame). One storm is active at a
 * time, chosen by proximity to the camera; the shader blends its frame
 * over the global field inside the frame's box.
 *
 * Past the newest observation the picture hands off to the forecast
 * (CloudForecastDatasetLayer, `forecastWeight`): the global field
 * dissolves into the ECMWF model-cloud frames (a second flow-advected
 * series mixed in by the field baker) and the region path carries the
 * RRFS simulated-satellite frames over CONUS, once the observed detail
 * sources have let go.
 */
export class CloudsOverlay extends TextureOverlay {
	private flowSeries: PrecipFlowTextureSeries
	private fieldBaker: CloudFieldBaker
	private regionSeries: RegionCloudTextureSeries<StormCloudTextureFrame>
	private forecastSeries: RegionCloudTextureSeries<CloudRegionFrame>
	// Global forecast frames (ECMWF cloud, 3-hourly), advected like the
	// observed series and mixed over it in the bake
	private globalForecastSeries: PrecipFlowTextureSeries
	private mosaic: CloudMosaicWindow
	private stormFrames = new Map<string, StormCloudTextureFrame[]>()
	private stormCenters = new Map<string, { lat: number, lon: number }>()
	private activeStormId?: string
	// What the shader's region path shows this frame (forecast past the
	// observations, else storm cutout when one is active and loaded, else
	// the mosaic window, else nothing) and its wall-clock fade
	private regionBinding?: CloudRegionBinding
	private regionFade = 0
	private lastFadeMs = 0
	/** Whether the forecast layer is on (set by CloudForecastDatasetLayer) */
	public forecastEnabled: () => boolean = () => false
	/** 0 while the clock is within the observations, 1 once the forecast owns the region path */
	public forecastWeight = 0

	private context: EarthBrowserContext

	constructor(context: EarthBrowserContext) {
		super(context)
		this.context = context
		this.flowSeries = new PrecipFlowTextureSeries(context)
		this.flowSeries.debugLabel = "clouds"
		// The tile shader samples the baked field, not the frame pair, so
		// the series needn't pack one (the field replaces the ~16 MB pair)
		this.flowSeries.packPairs = false
		this.textureSeries = this.flowSeries
		this.fieldBaker = new CloudFieldBaker(context)

		// Live mode sits a scan or two past the newest cutout; hold it that
		// long, then let the global frame take over if the feed stalls
		this.regionSeries = new RegionCloudTextureSeries<StormCloudTextureFrame>(context, timedelta.minutes(40))
		this.regionSeries.debugLabel = "storm-clouds"
		// The forecast's last hour stands until the window runs out
		this.forecastSeries = new RegionCloudTextureSeries<CloudRegionFrame>(context, timedelta.hours(1))
		this.forecastSeries.debugLabel = "cloud-forecast"
		this.globalForecastSeries = new PrecipFlowTextureSeries(context)
		this.globalForecastSeries.debugLabel = "cloud-forecast-global"
		this.globalForecastSeries.packPairs = false
		this.mosaic = new CloudMosaicWindow(context)

		registerCloudDetailProvider(() => ({
			source: this.regionBinding ? (this.forecastWeight > 0 ? "forecast" : this.stormBinding() ? "storm" : "mosaic") : "none",
			fade: this.regionFade,
			forecastWeight: this.forecastWeight,
			activeStorm: this.activeStormId,
			storms: [...this.stormFrames.keys()],
			mosaic: this.mosaic.debugSnapshot(),
		}))
	}

	public overlayMaterial(): Material {
		return new CloudsOverlayMaterial(this.flowSeries, this.fieldBaker.field, () => this.regionBinding, () => this.regionFade * (this.forecastWeight > 0 ? this.forecastWeight : 1))
	}

	/** Replace the regional (RRFS) forecast frames (chronological); empty clears them */
	public setForecastFrames(frames: CloudRegionFrame[]): void {
		this.forecastSeries.setFrames(frames)
	}

	/** Replace the global (ECMWF) forecast frames (chronological); empty clears them */
	public setGlobalForecastFrames(frames: RadarTextureFrame[]): void {
		this.globalForecastSeries.setFrames(frames)
	}

	/** Time (ms) of the newest observed frame across the global series, the
	 *  mosaic and the storm cutouts; undefined with nothing loaded */
	public get latestObservedMs(): number | undefined {
		const candidates = [
			this.flowSeries.frameTimesMs().at(-1),
			this.mosaic.frameTimesMs().at(-1),
			...[...this.stormFrames.values()].map(list => list.at(-1)?.range.start.date.getTime()),
		].filter((ms): ms is number => ms !== undefined)
		return candidates.length > 0 ? Math.max(...candidates) : undefined
	}

	/** Regional forecast series state for the dataset layer's diagnostics and lane */
	public get forecast(): TextureSeries<CloudRegionFrame> {
		return this.forecastSeries
	}

	/** Global forecast series state for the dataset layer's diagnostics and lane */
	public get globalForecast(): TextureSeries<RadarTextureFrame> {
		return this.globalForecastSeries
	}

	/** Replace the global mosaic frame list */
	public setMosaicFrames(rows: CloudMosaicFrameProperties[]): void {
		this.mosaic.setFrames(rows)
	}

	/** Replace the storm cutout frames (all storms); the active storm's
	 *  series keeps frames whose URLs are unchanged */
	public setStormFrames(frames: StormCloudTextureFrame[]): void {
		this.stormFrames.clear()
		this.stormCenters.clear()
		for (const frame of frames) {
			const list = this.stormFrames.get(frame.stormId) ?? []
			list.push(frame)
			this.stormFrames.set(frame.stormId, list)
		}
		for (const [stormId, list] of this.stormFrames) {
			list.sort((a, b) => a.range.start.julian - b.range.start.julian)
			// Newest box center stands in for the storm's position
			const newest = list[list.length - 1].bounds
			this.stormCenters.set(stormId, {
				lon: ((newest.x + newest.z) * 0.5) * 360 - 180,
				lat: 90 - ((newest.y + newest.w) * 0.5) * 180,
			})
		}
		if (this.activeStormId !== undefined) {
			const active = this.stormFrames.get(this.activeStormId)
			if (active) {
				this.regionSeries.setFrames(active)
			} else {
				this.activeStormId = undefined
				this.regionSeries.clear()
			}
		}
	}

	public override purge(): void {
		super.purge()
		this.fieldBaker.reset()
		this.regionSeries.purgeAll()
		this.forecastSeries.purgeAll()
		this.globalForecastSeries.purgeAll()
		this.mosaic.purge()
	}

	/** Free the forecast frames' textures (the forecast layer was hidden) */
	public purgeForecast(): void {
		this.forecastSeries.purgeAll()
		this.globalForecastSeries.purgeAll()
	}

	public override frameUpdate(state: RenderState): void {
		super.frameUpdate(state)
		this.selectStorm(state)
		this.regionSeries.updateState(state)

		// Forecast hand-off weight: 0 until the clock is past the newest
		// observation by the detail sources' hold, then up over the span
		this.forecastWeight = 0
		const forecastReady = this.forecastEnabled() && (this.forecastSeries.timeRange !== undefined || this.globalForecastSeries.timeRange !== undefined)
		if (forecastReady) {
			this.forecastSeries.updateState(state)
			this.globalForecastSeries.updateState(state)
			const clockMs = this.context.clock.clockTime.date.getTime()
			const cutoff = (this.latestObservedMs ?? Date.now()) + cForecastLagMs
			const t = clamp((clockMs - cutoff) / cForecastSpanMs, 0, 1)
			this.forecastWeight = t * t * (3 - 2 * t)
		}

		// Series state (frame pair, blend, flow) is current after the
		// updates above; rebake the field if any of it moved. The global
		// forecast pair rides along, mixed in by the hand-off weight
		const series = this.flowSeries
		const global = this.globalForecastSeries
		const forecastPair = this.forecastWeight > 0 && global.texture0.available
			? { frame0: global.texture0, frame1: global.texture1, flow: global.flowTexture, blend: global.blendFactor, weight: this.forecastWeight }
			: undefined
		this.fieldBaker.update(series.texture0, series.texture1, series.flowTexture, series.blendFactor, forecastPair)

		// Forecast once it owns the clock; else storm cutout first (single
		// best satellite, no seams), else the mosaic window around the camera
		this.regionBinding = this.forecastWeight > 0
			? this.forecastSeries.binding()
			: this.stormBinding() ?? this.mosaic.update(state)

		// Fade the region in once something is ready to show. With nothing
		// to show it drops out at once rather than fading: the textures it
		// would fade over may already be destroyed (purged frame, evicted
		// window), and both sources hold their last good texture while a
		// replacement loads, so this only happens when detail truly ends
		const nowMs = performance.now()
		const dt = this.lastFadeMs > 0 ? Math.min((nowMs - this.lastFadeMs) / 1000, 0.1) : 0
		this.lastFadeMs = nowMs
		this.regionFade = this.regionBinding ? clamp(this.regionFade + dt / cRegionFadeSeconds, 0, 1) : 0
	}

	/** The active storm's frame pair, once its displayed frame is loaded */
	private stormBinding(): CloudRegionBinding | undefined {
		return this.activeStormId === undefined ? undefined : this.regionSeries.binding()
	}

	/** Pick the storm nearest the camera's ground point, with hysteresis */
	private selectStorm(state: RenderState): void {
		if (this.stormCenters.size === 0) {
			return
		}
		const here = new geographic(state.camera.position)
		const hereLat = here.latitude * trig.deg
		const hereLon = here.longitude * trig.deg

		let bestId: string | undefined
		let bestDeg = cRegionSelectDeg
		let activeDeg = Number.POSITIVE_INFINITY
		for (const [stormId, center] of this.stormCenters) {
			const deg = angularDistanceDeg(hereLat, hereLon, center.lat, center.lon)
			if (stormId === this.activeStormId) {
				activeDeg = deg
			}
			if (deg < bestDeg) {
				bestDeg = deg
				bestId = stormId
			}
		}

		if (this.activeStormId !== undefined) {
			if (activeDeg <= cRegionSelectDeg && (bestId === this.activeStormId || bestDeg > activeDeg - cRegionHysteresisDeg)) {
				return
			}
		}
		if (bestId === this.activeStormId) {
			return
		}
		this.activeStormId = bestId
		if (bestId !== undefined) {
			this.regionSeries.setFrames(this.stormFrames.get(bestId) ?? [])
		} else {
			this.regionSeries.clear()
		}
	}
}

function angularDistanceDeg(lat0: number, lon0: number, lat1: number, lon1: number): number {
	const a0 = lat0 * trig.rad
	const a1 = lat1 * trig.rad
	const dlon = (lon1 - lon0) * trig.rad
	const c = Math.sin(a0) * Math.sin(a1) + Math.cos(a0) * Math.cos(a1) * Math.cos(dlon)
	return Math.acos(clamp(c, -1, 1)) * trig.deg
}

const cNoBounds = new vec4(0, 0, 0, 0)

class CloudsOverlayMaterial implements Material {
	public shaderDefines: Record<string, string> = {
		"CLOUD_TEXTURES": "1"
	}
	public available: boolean = true
	public transparency: boolean = false
	public cloudFade = new UniformDynamic<number>()
	// Baked advected blend of the current frame pair (CloudFieldBaker)
	public cloudField = new UniformDynamic<Texture>()
	// Detail region pair (storm cutout or mosaic window): two textures with
	// their own boxes (globe UV bounds), crossfaded by cloudRegionBlend;
	// cloudRegionFade is 0 when there is nothing to show
	public cloudRegion0 = new UniformDynamic<Texture>()
	public cloudRegion1 = new UniformDynamic<Texture>()
	public cloudRegionBounds0 = new UniformDynamic<vec4>()
	public cloudRegionBounds1 = new UniformDynamic<vec4>()
	public cloudRegionBlend = new UniformDynamic<number>()
	public cloudRegionFade = new UniformDynamic<number>()
	public cloudRegionTexel = new UniformDynamic<number>()
	/** 0 = clean white clouds, 1 = enhanced-IR palette with a fixed key light (render mode) */
	public cloudStyle = new UniformDynamic<number>()

	private textureSeries: PrecipFlowTextureSeries
	private field: Texture
	private region: () => CloudRegionBinding | undefined
	private regionFade: () => number
	private blank: Texture

	constructor(textureSeries: PrecipFlowTextureSeries, field: Texture, region: () => CloudRegionBinding | undefined, regionFade: () => number) {
		this.textureSeries = textureSeries
		this.field = field
		this.region = region
		this.regionFade = regionFade
		this.blank = textureSeries.texture0
	}

	public renderUpdate(_state: RenderState): void {
		this.cloudFade.value = this.textureSeries.fadeFactor
		this.cloudField.value = this.field
		this.cloudStyle.value = cloudStyle() === "ir" ? 1 : 0

		const region = this.region()
		if (region) {
			this.cloudRegion0.value = region.texture0
			this.cloudRegion1.value = region.texture1
			this.cloudRegionBounds0.value = region.bounds0
			this.cloudRegionBounds1.value = region.bounds1
			this.cloudRegionBlend.value = region.blend
			this.cloudRegionFade.value = this.regionFade()
			this.cloudRegionTexel.value = region.texel
		} else {
			// Nothing to show: bind a live texture (the global pair) so the
			// samplers stay valid, and zero bounds so the shader's region
			// weight is 0 regardless of fade
			this.cloudRegion0.value = this.blank
			this.cloudRegion1.value = this.blank
			this.cloudRegionBounds0.value = cNoBounds
			this.cloudRegionBounds1.value = cNoBounds
			this.cloudRegionBlend.value = 0
			this.cloudRegionFade.value = 0
			this.cloudRegionTexel.value = 1 / 2048
		}
	}
}
